import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { GLCode, Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';

import { useCurrentUserStore } from './CurrentUser';
import { useParentEntityStore } from './ParentEntity';

export interface PerformLoadOptions {
  bypassCache?: boolean;
}

export interface GLCodeStore {
  availableGLCodes: Ref<Loadable<GLCode[]>>;
  loadGLCodes: (opts?: PerformLoadOptions) => Promise<void>;
}

const PERFORM_LOAD_DEFAULT_OPTIONS = {
  bypassCache: false,
};

const _useGLCodeStore = defineStore('glCode', (): GLCodeStore => {
  const { selectedDomainId } = useCurrentUserStore();
  const { t } = useI18n();
  const { includeSubDomains } = useParentEntityStore();
  const toast = useToast();

  const availableGLCodes: Ref<Loadable<GLCode[]>> = ref({
    loading: false,
  });

  const loadGLCodes = async (opts: PerformLoadOptions = {}) => {
    const options: Required<PerformLoadOptions> = {
      ...PERFORM_LOAD_DEFAULT_OPTIONS,
      ...opts,
    };

    const numEquipmentTypes = availableGLCodes.value.data?.length ?? 0;
    const isLoading = availableGLCodes.value.loading;
    const requiresLoad = numEquipmentTypes === 0 && !isLoading;

    if (options.bypassCache || requiresLoad) {
      return performLoad();
    }
  };

  const performLoad = async () => {
    try {
      availableGLCodes.value.loading = true;
      availableGLCodes.value.data =
        await api.referenceNumberType.findAllGlCodes();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      availableGLCodes.value.error = e;
      console.error('Error loading gl codes', e);
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('glCodes.glCodes', 2),
        }),
        error: e,
      });
    } finally {
      availableGLCodes.value.loading = false;
    }
  };

  const reset = () => {
    availableGLCodes.value.data = [];
  };

  watch(selectedDomainId, () => {
    reset();
  });

  watch(includeSubDomains, () => {
    reset();
  });

  return {
    availableGLCodes,
    loadGLCodes,
  };
});

export const useGLCodeStore = (): GLCodeStore => {
  const store = _useGLCodeStore();
  const { availableGLCodes } = storeToRefs(store);
  const { loadGLCodes } = store;

  return {
    availableGLCodes,
    loadGLCodes,
  };
};
